import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MenuItem, Box, Typography, Checkbox, FormControlLabel } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import GoldFormDialog, { GoldFormBtn } from "./GoldFormDialog";
import AppSelect from "./AppSelect";
import GoldField from "./GoldField";
import { GOLD_THEME } from "./goldTheme";
import { API_BASE_URL } from "../../utils/config";
import { notifyError } from "../../helper/notify";

// ═══════════════════════════════════════════════════════════════════════
//  "Buy extra AI credits" — ONE pop-up used by Ask AI (the chat), AI Agents and AI Studio. A customer can buy at ANY time (the credits do not
//  have to be finished) and with ANY set of modules. The credits go into the same monthly pool all three spend from and are valid for this
//  calendar month. Two ways to pay (the server decides which the user may use):
//    · Wallet — only for a user who holds the Wallet & Charges module (the balance is shown)
//    · Card   — a saved card, or a new one typed here (name, number, expiry, CVC; "save for next time" is optional)
//  The card number and CVC go to the server once, to be turned into the payment provider's token; they are never stored. Until a payment account
//  is connected the server runs in TEST MODE: the card is checked but nothing is charged. When the payment goes through the pop-up shows the
//  receipt (credits added, how it was paid, the invoice number) — the same invoice + notification + email as a plan purchase.
//    <BuyCreditsDialog open={open} onClose={() => setOpen(false)} onBought={(result) => reload()} zIndex={1600} />
// ═══════════════════════════════════════════════════════════════════════

const n = (v) => Number(v || 0).toLocaleString("en-US");
const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const API = `${API_BASE_URL}/api/ai-credits`;

const groupDigits = (v, brandAmex) => {
  const d = String(v).replace(/\D/g, "").slice(0, brandAmex ? 15 : 19);
  return brandAmex ? [d.slice(0, 4), d.slice(4, 10), d.slice(10)].filter(Boolean).join(" ") : d.replace(/(.{4})/g, "$1 ").trim();
};
const fixExpiry = (v) => {
  const d = String(v).replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};
const EMPTY_CARD = { holder: "", number: "", expiry: "", cvc: "" };
const cardLabel = (c) => `${c.brand} •••• ${c.last4}  ·  expires ${String(c.exp_month).padStart(2, "0")}/${String(c.exp_year).slice(-2)}`;

export default function BuyCreditsDialog({ open, onClose, onBought, zIndex }) {
  const token = useSelector((s) => s.auth.token);
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [packs, setPacks] = useState(1);
  const [method, setMethod] = useState("card");          // "wallet" | "card"
  const [cardId, setCardId] = useState("new");           // a saved card's id, or "new"
  const [card, setCard] = useState(EMPTY_CARD);
  const [saveCard, setSaveCard] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);                // the receipt once paid
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true); setErr(""); setPacks(1); setDone(null); setCard(EMPTY_CARD); setSaveCard(true);
    axios.get(`${API}/pack`, { headers })
      .then((r) => {
        if (!live) return;
        const d = r.data; setInfo(d);
        const def = (d.cards || []).find((c) => c.is_default) || (d.cards || [])[0];
        setCardId(def ? def.id : "new");
        setMethod(d.can_use_wallet && d.wallet_balance >= d.pack.price ? "wallet" : "card");
      })
      .catch((e) => { if (live) setErr(e.response?.data?.message || "Could not load the credit pack."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const total = info ? info.pack.credits * packs : 0;
  const price = info ? Math.round(info.pack.price * packs * 100) / 100 : 0;
  const walletEnough = info ? (info.wallet_balance || 0) + 0.000001 >= price : false;
  const amex = /^3[47]/.test(card.number.replace(/\s/g, ""));
  const newCard = method === "card" && cardId === "new";
  const cardReady = !newCard || (card.holder.trim().length > 1 && card.number.replace(/\s/g, "").length >= 13 && /^\d{2}\/\d{2}$/.test(card.expiry) && card.cvc.length >= 3);
  const canPay = !!info && info.on_sale && !busy && (method === "wallet" ? walletEnough : (info.card_enabled && cardReady));
  const setField = (k, v) => { setErr(""); setCard((c) => ({ ...c, [k]: v })); };

  const pay = async () => {
    setBusy(true); setErr("");
    try {
      const body = { packs, method };
      if (method === "card") {
        if (cardId !== "new") body.card_id = cardId;
        else { body.card = { holder: card.holder.trim(), number: card.number, expiry: card.expiry, cvc: card.cvc }; body.save_card = saveCard; }
      }
      const { data } = await axios.post(`${API}/buy`, body, { headers });
      setCard(EMPTY_CARD);                                // the number and CVC are not kept in the page either
      setDone(data);
      if (onBought) onBought(data);
    } catch (e) {
      const m = e.response?.data?.message || "Could not complete the payment. Please try again.";
      setErr(m); notifyError(m);
    } finally { setBusy(false); }
  };

  const row = (label, value, strong) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.7, borderBottom: "1px solid rgba(184,137,44,0.22)" }}>
      <Typography sx={{ fontSize: "0.86rem", color: GOLD_THEME.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: strong ? 800 : 700, color: GOLD_THEME.text, textAlign: "right" }}>{value}</Typography>
    </Box>
  );

  const tile = (key, Icon, title, sub, disabled) => {
    const on = method === key;
    return (
      <Box component="button" type="button" disabled={disabled} onClick={() => { setErr(""); setMethod(key); }}
        sx={{ flex: 1, minWidth: 0, textAlign: "left", cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", p: 1.2, borderRadius: "12px", opacity: disabled ? 0.55 : 1,
          border: `1.5px solid ${on ? GOLD_THEME.goldDark : "rgba(184,137,44,0.35)"}`, background: on ? "rgba(184,137,44,0.12)" : "#fffcf7", display: "flex", gap: 1, alignItems: "center" }}>
        <Icon sx={{ color: GOLD_THEME.goldDark, fontSize: 24 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.86rem", color: GOLD_THEME.text }}>{title}</Typography>
          <Typography sx={{ fontSize: "0.74rem", color: GOLD_THEME.muted }}>{sub}</Typography>
        </Box>
      </Box>
    );
  };

  const savedCards = useMemo(() => (info && info.cards) || [], [info]);

  // ── the receipt after a successful payment ──
  if (done) {
    return (
      <GoldFormDialog open={!!open} onClose={onClose} width={520} badge={null} zIndex={zIndex} title="Payment successful" subtitle="Your extra AI credits are ready to use"
        footer={<GoldFormBtn variant="primary" onClick={onClose}>Done</GoldFormBtn>}>
        <Box sx={{ textAlign: "center", pb: 1 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 54, color: "#15803d" }} />
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: GOLD_THEME.text, mt: 0.4 }}>{n(done.bought)} AI credits added</Typography>
          <Typography sx={{ fontSize: "0.82rem", color: GOLD_THEME.muted, mt: 0.3 }}>For this month. They work in Ask AI, AI Agents and AI Studio.</Typography>
        </Box>
        <Box sx={{ mt: 1.2 }}>
          {row("Paid", money(done.price), true)}
          {row("Paid with", done.paid_with === "wallet" ? "MarketSync wallet" : `${done.card.brand} •••• ${done.card.last4}`)}
          {done.invoice && row("Invoice", done.invoice)}
          {row("Credits left now", `${n(done.credits.remaining)} of ${n(done.credits.limit)}`, true)}
        </Box>
        <Typography sx={{ fontSize: "0.78rem", color: GOLD_THEME.muted, mt: 1.2 }}>A receipt was sent to your notifications and your email.</Typography>
      </GoldFormDialog>
    );
  }

  return (
    <GoldFormDialog open={!!open} onClose={() => { if (!busy) onClose(); }} width={560} badge={null} zIndex={zIndex}
      title="Buy extra AI credits" subtitle={info ? `${n(info.pack.credits)} credits for ${money(info.pack.price)} a pack` : "Top up your AI credits"}
      footer={(
        <>
          <GoldFormBtn variant="secondary" disabled={busy} onClick={onClose}>Cancel</GoldFormBtn>
          <GoldFormBtn variant="primary" loading={busy} disabled={!canPay} onClick={pay}>{info ? `Pay ${money(price)}` : "Pay"}</GoldFormBtn>
        </>
      )}>
      <Box sx={{ pb: 1 }}>
        {loading && <Typography sx={{ fontSize: "0.86rem", color: GOLD_THEME.muted, py: 2 }}>Loading…</Typography>}
        {err && !info && <Typography sx={{ fontSize: "0.86rem", color: "#b91c1c", py: 1 }}>{err}</Typography>}
        {info && (
          <>
            <Typography sx={{ fontSize: "0.86rem", lineHeight: 1.6, color: GOLD_THEME.text, mb: 1.6 }}>
              You can buy more credits <b>at any time</b>, even when you still have some left. They are added to <b>this month&apos;s credits</b> and are used by <b>Ask AI, AI Agents and AI Studio</b> together. They end with this month.
            </Typography>
            {!info.on_sale ? (
              <Typography sx={{ fontSize: "0.86rem", color: "#b91c1c" }}>Extra credits are not on sale right now.</Typography>
            ) : (
              <>
                <AppSelect label="How many packs" value={packs} onChange={(e) => setPacks(Number(e.target.value))}>
                  {Array.from({ length: info.max_packs }, (_, i) => i + 1).map((p) => (
                    <MenuItem key={p} value={p}>{`${p} pack${p === 1 ? "" : "s"} - ${n(info.pack.credits * p)} credits - ${money(info.pack.price * p)}`}</MenuItem>
                  ))}
                </AppSelect>

                <Typography sx={{ fontWeight: 800, fontSize: "0.84rem", color: GOLD_THEME.text, mt: 1.8, mb: 0.8 }}>Pay with</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {info.can_use_wallet && tile("wallet", AccountBalanceWalletOutlinedIcon, "Wallet", `Balance ${money(info.wallet_balance)}`, false)}
                  {tile("card", CreditCardIcon, "Card", savedCards.length ? `${savedCards.length} saved card${savedCards.length === 1 ? "" : "s"}` : "Debit or credit card", !info.card_enabled)}
                </Box>

                {method === "wallet" && !walletEnough && (
                  <Box sx={{ mt: 1.2, p: 1.2, borderRadius: "12px", background: "rgba(194,65,12,0.08)", border: "1px solid rgba(194,65,12,0.35)" }}>
                    <Typography sx={{ fontSize: "0.84rem", color: "#9a3412", lineHeight: 1.5 }}>
                      Your wallet has {money(info.wallet_balance)} and this needs {money(price)}.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mt: 0.6, flexWrap: "wrap" }}>
                      {["Pay by card instead", "Top up the wallet"].map((t, i) => (
                        <Box key={t} component="button" type="button" onClick={() => { if (i === 0) setMethod("card"); else { onClose(); navigate("/marketplace-integration/wallet&charges"); } }}
                          sx={{ border: "none", background: "transparent", color: "#8a5a0a", fontWeight: 800, fontSize: "0.84rem", cursor: "pointer", p: 0, textDecoration: "underline", fontFamily: "inherit" }}>{t}</Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {method === "card" && (
                  <Box sx={{ mt: 1.4 }}>
                    {savedCards.length > 0 && (
                      <AppSelect label="Card" labelIcon={CreditCardIcon} value={cardId} onChange={(e) => { setErr(""); setCardId(e.target.value); }}>
                        {savedCards.map((c) => <MenuItem key={c.id} value={c.id}>{cardLabel(c)}</MenuItem>)}
                        <MenuItem value="new">Add a new card</MenuItem>
                      </AppSelect>
                    )}
                    {newCard && (
                      <Box sx={{ mt: savedCards.length ? 1.4 : 0, display: "grid", gap: 1.3 }}>
                        <GoldField label="Name on card" icon={PersonOutlineIcon} placeholder="As printed on the card" value={card.holder} autoComplete="cc-name"
                          onChange={(e) => setField("holder", e.target.value)} />
                        <GoldField label="Card number" icon={CreditCardIcon} placeholder="1234 5678 9012 3456" value={card.number} autoComplete="cc-number" inputProps={{ inputMode: "numeric" }}
                          onChange={(e) => setField("number", groupDigits(e.target.value, /^3[47]/.test(e.target.value.replace(/\s/g, ""))))} />
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.3 }}>
                          <GoldField label="Expiry" icon={EventOutlinedIcon} placeholder="MM/YY" value={card.expiry} autoComplete="cc-exp" inputProps={{ inputMode: "numeric", maxLength: 5 }}
                            onChange={(e) => setField("expiry", fixExpiry(e.target.value))} />
                          <GoldField label="CVC" icon={LockOutlinedIcon} placeholder={amex ? "4 digits" : "3 digits"} value={card.cvc} type="password" autoComplete="cc-csc" inputProps={{ inputMode: "numeric", maxLength: amex ? 4 : 3 }}
                            onChange={(e) => setField("cvc", e.target.value.replace(/\D/g, "").slice(0, amex ? 4 : 3))} />
                        </Box>
                        <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} sx={{ color: GOLD_THEME.goldDark, "&.Mui-checked": { color: GOLD_THEME.goldDark } }} />}
                          label={<Typography sx={{ fontSize: "0.82rem", color: GOLD_THEME.text }}>Save this card for next time</Typography>} />
                      </Box>
                    )}
                    <Typography sx={{ fontSize: "0.74rem", color: GOLD_THEME.muted, mt: 1 }}>
                      Your card number and security code are used once to make the payment and are never saved. We only keep the card type and its last 4 digits.
                    </Typography>
                    {info.test_mode && (
                      <Box sx={{ mt: 1, p: 1, borderRadius: "10px", background: "rgba(184,137,44,0.1)", border: "1px dashed rgba(184,137,44,0.55)" }}>
                        <Typography sx={{ fontSize: "0.78rem", color: "#7a5712", lineHeight: 1.5 }}>
                          <b>Test mode:</b> the payment account is not connected yet, so your card is checked but <b>no money is taken</b>. The credits, the receipt, the notification and the email work exactly as they will later.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <Box sx={{ mt: 1.6 }}>
                  {row("You get", `${n(total)} credits`, true)}
                  {row("You pay", money(price), true)}
                  {row("Credits left now", `${n(info.credits.remaining)} of ${n(info.credits.limit)}`)}
                  {row("Credits left after", `${n(info.credits.remaining + total)} of ${n(info.credits.limit + total)}`, true)}
                </Box>
                {err && <Typography sx={{ fontSize: "0.84rem", color: "#b91c1c", mt: 1.2, fontWeight: 700 }}>{err}</Typography>}
              </>
            )}
          </>
        )}
      </Box>
    </GoldFormDialog>
  );
}
